import { Fragment } from "react";

import { Progress } from "@/components/ui/progress";
import { formatPct0 } from "@/lib/pilotageUi";

const clampProgress = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Math.max(0, Math.min(100, numericValue));
};

const formatMetricValue = (metric, value) => {
  if (value === null || value === undefined) {
    return "— non disponible";
  }
  const formatted = metric.formatter(value);
  if (formatted === "—") {
    return "— non disponible";
  }
  if (value < 0) {
    return <span className="text-red-400">{formatted}</span>;
  }
  if (value > 0 && metric.allowNegative) {
    return <span className="text-emerald-400">{formatted}</span>;
  }
  return formatted;
};

const groupMetrics = (metrics) => {
  const grouped = [];
  const groupMap = new Map();

  for (const metric of metrics) {
    if (!groupMap.has(metric.group)) {
      groupMap.set(metric.group, {
        group: metric.group,
        items: [],
      });
      grouped.push(groupMap.get(metric.group));
    }

    groupMap.get(metric.group).items.push(metric);
  }

  return grouped;
};

const ObjectivesKpiTable = ({ metrics, loading, objectiveName }) => {
  const groupedMetrics = groupMetrics(metrics);

  return (
    <section
      className="overflow-hidden rounded-3xl border border-white/10 glass"
      data-testid="pilotage-objectives-table"
      aria-labelledby="objectives-kpi-table-title"
    >
      <div className="border-b border-white/10 bg-white/[0.03] px-4 py-4 sm:px-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          Suivi des indicateurs
        </p>
        <h2 id="objectives-kpi-table-title" className="mt-1 text-xl font-heading font-semibold text-white">
          Performance cible vs actuel
        </h2>
      </div>

      <div className="overflow-x-auto min-w-0" data-testid="pilotage-objectives-table-scroll">
        <table className="min-w-full divide-y divide-white/10">
          <thead className="bg-white/5">
            <tr>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 sm:px-6">
                Indicateur
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 sm:px-6">
                Objectif
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 sm:px-6">
                Actuel
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 sm:px-6">
                Progression
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-white/5">
            {loading ? (
              <tr>
                <td className="px-4 py-8 text-sm text-slate-400 sm:px-6" colSpan={4}>
                  Chargement des indicateurs...
                </td>
              </tr>
            ) : (
              groupedMetrics.map((group) => (
                <Fragment key={group.group}>
                  <tr className="bg-white/[0.02]">
                    <td className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 sm:px-6" colSpan={4}>
                      {group.group}
                    </td>
                  </tr>
                  {group.items.map((metric) => {
                    const progressValue = clampProgress(metric.percent);

                    return (
                      <tr key={metric.key} className="hover:bg-white/[0.03]">
                        <td className="px-4 py-3 align-top text-sm font-medium text-white sm:px-6">
                          {metric.label}
                        </td>
                        <td className="px-4 py-3 align-top text-sm text-slate-200 sm:px-6">
                          <div>{formatMetricValue(metric, metric.target)}</div>
                          {metric.target !== null && metric.target !== undefined && objectiveName && (
                            <div className="mt-1 text-xs text-slate-400">{objectiveName}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top text-sm text-slate-200 sm:px-6">
                          {formatMetricValue(metric, metric.realized)}
                        </td>
                        <td className="px-4 py-3 align-top text-sm text-slate-200 sm:px-6">
                          {metric.percent === null || metric.percent === undefined ? (
                            <span className="text-slate-400">— non disponible</span>
                          ) : (
                            <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
                              <Progress
                                value={progressValue ?? 0}
                                className="h-2.5 w-full min-w-0 flex-1 bg-white/10 sm:max-w-[180px]"
                                aria-label={`Progression ${metric.label}`}
                              />
                              <span className="font-semibold text-white">{formatPct0(metric.percent)}</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default ObjectivesKpiTable;
